import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import LeftPanel from '../components/LeftPanel';
import PrimaryButton from '../components/PrimaryButton';
import InfoModal from '../components/InfoModal';
import SCHOOL_PROFILE_SECTIONS from '../data/schoolProfileFields';
import LogoUpload from '../components/LogoUpload';
import Button from '@mui/material/Button';
import Step from '../components/Step';
import ConfirmSubmissionModal from '../components/ConfirmSubmissionModal';
import useUnsavedChangesWarning from '../hooks/useUnsavedChangesWarning';
import UnsavedChangesModal from '../components/UnsavedChangesModal';
// states are fetched from the server at runtime

function SchoolInfo() {
    const SPECIAL_CONFERENCE_IDS = new Set([61, 65]); // IDs to pin to bottom and highlight
    // Assume user context or API provides school name
    const [schoolName, setSchoolName] = useState('');
    const [schoolInfoStatus, setSchoolInfoStatus] = useState('not_started');
    const [lastUpdated, setLastUpdated] = useState(null);
    const [conferences, setConferences] = useState([]);
    const [statesList, setStatesList] = useState([]);
    const [profile, setProfile] = useState({
        tax_id: '', accreditation_status: '', school_logo: '',
        contact_first_name: '', contact_last_name: '', contact_title: '', contact_department: '', contact_email: '', contact_phone: '',
        president_first_name: '', president_last_name: '', president_office_phone: '', president_email: '',
        secondary_contact_first_name: '', secondary_contact_last_name: '', secondary_contact_title: '', secondary_contact_department: '', secondary_contact_email: '', secondary_contact_phone: '',
        school_colors: '',
        shipping_attention_first_name: '', shipping_attention_last_name: '', shipping_attention_phone: '',
        shipping_address_line1: '', shipping_address_line2: '', shipping_city: '', shipping_state: '', shipping_zip: '',
        athletic_conference_id: ''
    });
    const [touched, setTouched] = useState({});
    const [serverError, setServerError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [isExisting, setIsExisting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [infoOpen, setInfoOpen] = useState(false);
    const [infoType, setInfoType] = useState('faq');

    const initialSnapshot = useRef(JSON.stringify(profile));
    const isDirty = () => {
        try {
            if (submitting) return false;
            return JSON.stringify(profile) !== (initialSnapshot.current || JSON.stringify({}));
        } catch (e) { return false; }
    };
    const pendingResolve = useRef(null);
    const [unsavedModalOpen, setUnsavedModalOpen] = useState(false);
    const showPrompt = () => new Promise((resolve) => {
        pendingResolve.current = resolve;
        setUnsavedModalOpen(true);
    });
    const handleModalClose = () => {
        setUnsavedModalOpen(false);
        if (pendingResolve.current) { pendingResolve.current(false); pendingResolve.current = null; }
    };
    const handleModalConfirm = () => {
        setUnsavedModalOpen(false);
        if (pendingResolve.current) { pendingResolve.current(true); pendingResolve.current = null; }
    };

    useUnsavedChangesWarning(isDirty, 'You have unsaved changes on the School Information form. Save draft or cancel to continue.', showPrompt);

    const attemptSubmit = () => {
        const newTouched = {};
        requiredFields.forEach(f => newTouched[f] = true);
        setTouched(newTouched);
        const errs = validateAll();
        if (Object.keys(errs).length > 0) {
            setSnackbarMessage('Please complete required fields');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            const first = Object.keys(errs)[0];
            try {
                const el = document.querySelector(`[name="${first}"]`);
                if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus({ preventScroll: true }); }
            } catch (e) { }
            return;
        }
        setConfirmOpen(true);
    };
    const navigate = useNavigate();
    const sectionSx = { border: '1px solid #f0f0f0', p: 2, mb: 2, background: '#fff' };

    const handleChange = (e) => {
        const { name, value } = e.target;
        // format phone numbers as user types
        const isPhone = String(name).toLowerCase().includes('phone');
        const isTax = name === 'tax_id';
        let newValue = value;
        if (isPhone) newValue = formatPhone(value);
        else if (isTax) newValue = formatTaxId(value);
        setProfile((p) => ({ ...p, [name]: newValue }));
        setFieldErrors((f) => ({ ...f, [name]: undefined }));
    };

    // Get JWT token for authenticated requests
    const getToken = () => localStorage.getItem('token');

    const formatPhone = (val) => {
        if (!val) return '';
        const digits = String(val).replace(/\D/g, '').slice(0, 10);
        const len = digits.length;
        if (len === 0) return '';
        if (len < 4) return digits;
        if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    };

    const TAX_ID_REGEX = /^\d{2}-\d{7}$/;
    const formatTaxId = (val) => {
        if (!val) return '';
        const digits = String(val).replace(/\D/g, '').slice(0, 9);
        if (digits.length <= 2) return digits;
        return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    };

    const handleTaxIdPaste = (e) => {
        try {
            const text = (e.clipboardData && e.clipboardData.getData('text')) || '';
            const digits = String(text).replace(/\D/g, '').slice(0, 9);
            const formatted = digits.length <= 2 ? digits : `${digits.slice(0, 2)}-${digits.slice(2)}`;
            e.preventDefault();
            setProfile(p => ({ ...p, tax_id: formatted }));
            setTouched(t => ({ ...t, tax_id: true }));
            setFieldErrors(f => ({ ...f, tax_id: undefined }));
        } catch (err) {
            // fallback: do nothing special
        }
    };

    const formatToEastern = (iso) => {
        if (!iso) return '';
        try {
            const d = new Date(iso);
            const datePart = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' });
            const timePart = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'America/New_York' });
            return `${datePart} - ${timePart} Eastern`;
        } catch (e) {
            return iso;
        }
    };

    const getSchoolInfoStatusLabel = () => {
        if (schoolInfoStatus === 'draft') return 'Status - Draft (Not Submitted)';
        if (schoolInfoStatus === 'submitted') return 'Status - Submitted';
        return 'Status - Not Started';
    };

    const showSchoolInfoWarning = schoolInfoStatus !== 'submitted';

    const handleBlur = (field) => {
        setTouched((t) => ({ ...t, [field]: true }));
        // validate Tax ID on blur
        if (field === 'tax_id') {
            const val = profile.tax_id || '';
            if (val && !TAX_ID_REGEX.test(String(val))) {
                setFieldErrors((f) => ({ ...f, tax_id: 'Tax ID must be in format XX-XXXXXXX' }));
            } else {
                setFieldErrors((f) => ({ ...f, tax_id: undefined }));
            }
        }
    };

    const requiredFields = React.useMemo(() => {
        const list = [];
        SCHOOL_PROFILE_SECTIONS.forEach(section => {
            section.fields.forEach(f => { if (f.required) list.push(f.name); });
        });
        return list;
    }, []);

    const conferenceOptions = React.useMemo(() => {
        const list = Array.isArray(conferences) ? [...conferences] : [];
        list.sort((a, b) => {
            const aIsSpecial = SPECIAL_CONFERENCE_IDS.has(Number(a?.id));
            const bIsSpecial = SPECIAL_CONFERENCE_IDS.has(Number(b?.id));
            if (aIsSpecial !== bIsSpecial) return aIsSpecial ? 1 : -1;
            // Within special IDs, sort 61 before 65; non-special sorts alphabetically
            if (aIsSpecial && bIsSpecial) return Number(a?.id) - Number(b?.id);
            return String(a?.name || '').localeCompare(String(b?.name || ''));
        });
        return list;
    }, [conferences, SPECIAL_CONFERENCE_IDS]);

    const errorSx = {
        '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'error.main' } },
        '& .MuiFormLabel-root': { color: 'error.main' }
    };
    const requiredAsteriskSx = { '& .MuiFormLabel-asterisk': { color: 'warning.main' } };
    const fieldSx = (name, base = {}) => ({
        ...base,
        ...(fieldErrors && fieldErrors[name] ? errorSx : {}),
        ...(requiredFields && requiredFields.includes(name) ? requiredAsteriskSx : {})
    });

    const getHelperText = (name, defaultText = '') => {
        if (touched && touched[name] && fieldErrors && fieldErrors[name]) return fieldErrors[name];
        // If touched and required but empty, show Required
        if (touched && touched[name] && requiredFields && requiredFields.includes(name) && (!profile[name] || String(profile[name]).trim() === '')) return 'Required';
        return defaultText || '';
    };

    const validateAll = () => {
        const errs = {};
        requiredFields.forEach((f) => {
            if (!profile[f] || String(profile[f]).trim().length === 0) errs[f] = 'Required';
        });
        // Tax ID format validation (if present)
        if (profile.tax_id && String(profile.tax_id).trim().length > 0 && !TAX_ID_REGEX.test(String(profile.tax_id))) {
            errs['tax_id'] = 'Tax ID must be in format XX-XXXXXXX';
        }
        setFieldErrors(errs);
        return errs; // return errors object so caller can inspect and act on first invalid field
    };

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        // mark all fields touched and validate everything (final submit)
        const newTouched = {};
        requiredFields.forEach(f => newTouched[f] = true);
        setTouched(newTouched);
        const errs = validateAll();
        if (Object.keys(errs).length > 0) {
            // show quick feedback so user knows why submit did nothing
            setSnackbarMessage('Please complete required fields');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            // focus/scroll the first invalid field
            const first = Object.keys(errs)[0];
            try {
                const el = document.querySelector(`[name="${first}"]`);
                if (el) {
                    // if input is inside a scrollable container, scrolling the element should bring it into view
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    el.focus({ preventScroll: true });
                }
            } catch (e) { /* ignore */ }
            return;
        }
        try {
            setSubmitting(true);
            setServerError('');
            setSuccessMessage('');
            setFieldErrors({});
            // submit and mark as completed; always redirect after showing success
            await api.post('/api/public/school-profile/create', { ...profile, status: 'completed', last_saved_at: new Date().toISOString().slice(0, 19) });
            setIsExisting(true);
            // update snapshot to match saved state
            initialSnapshot.current = JSON.stringify(profile);
            localStorage.setItem('schoolProfileCompleted', 'true');
            setSuccessMessage(isExisting ? 'School information updated successfully.' : 'School information saved! Redirecting to grant selection...');
            // redirect after a short delay so the user sees the success snackbar
            try { window.dispatchEvent(new Event('formsUpdated')); } catch (e) { /* ignore */ }
            setTimeout(() => navigate('/grantlanding'), 2000);
        } catch (err) {
            const data = err?.response?.data;
            setSuccessMessage('');
            if (data?.fieldErrors) setFieldErrors(data.fieldErrors);
            else if (data?.message) setServerError(data.message);
            else setServerError('Failed to submit school info');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaveDraft = async () => {
        try {
            setSubmitting(true);
            setServerError('');
            setSuccessMessage('');
            setFieldErrors({});
            await api.post('/api/public/school-profile/create', { ...profile, status: 'draft', last_saved_at: new Date().toISOString().slice(0, 19) });
            setSchoolInfoStatus('draft');
            setIsExisting(true);
            // update snapshot to match saved draft
            initialSnapshot.current = JSON.stringify(profile);
            setSuccessMessage('Draft saved.');
            try { window.dispatchEvent(new Event('formsUpdated')); } catch (e) { /* ignore */ }
            // refresh profile to update header last-updated info
            try {
                const pr = await api.get('/api/public/school-profile');
                if (pr?.data?.exists) {
                    if (pr.data.last_updated_by_first_name || pr.data.updated_at || pr.data.last_saved_at) {
                        setLastUpdated({
                            firstName: pr.data.last_updated_by_first_name,
                            lastName: pr.data.last_updated_by_last_name,
                            email: pr.data.last_updated_by_email,
                            updatedAt: pr.data.updated_at || pr.data.last_saved_at
                        });
                    }
                    if (pr.data.schoolName) setSchoolName(pr.data.schoolName);
                }
            } catch (e) { /* ignore refresh error */ }
        } catch (err) {
            const data = err?.response?.data;
            if (data?.message) setServerError(data.message);
            else setServerError('Failed to save draft');
        } finally {
            setSubmitting(false);
        }
    };

    // show snackbars when messages change, and scroll to top so user sees them
    useEffect(() => {
        if (successMessage) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setSnackbarMessage(successMessage);
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setSuccessMessage('');
        }
    }, [successMessage]);

    useEffect(() => {
        if (serverError) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setSnackbarMessage(serverError);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setServerError('');
        }
    }, [serverError]);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('/api/public/me');
                if (res?.data?.authenticated) {
                    setSchoolName(res.data.schoolName || '');
                    setProfile(p => ({ ...p, contact_first_name: res.data.firstName || '', contact_last_name: res.data.lastName || '', contact_email: res.data.email || '' }));
                    // fetch existing school profile (if any) and prefill form
                    try {
                        const pr = await api.get('/api/public/school-profile');
                        if (pr?.data?.exists) {
                            const isSubmitted = Boolean(pr.data.completed);
                            const isDraft = String(pr.data.status || '').toLowerCase() === 'draft' || !isSubmitted;
                            setSchoolInfoStatus(isSubmitted ? 'submitted' : (isDraft ? 'draft' : 'not_started'));
                            // if profile already completed, redirect to grants
                            if (isSubmitted) {
                                navigate('/grantlanding');
                                return;
                            }
                            setProfile(p => ({
                                ...p,
                                tax_id: pr.data.tax_id || '',
                                accreditation_status: pr.data.accreditation_status || '',
                                school_logo: pr.data.school_logo || '',
                                contact_first_name: pr.data.contact_first_name || p.contact_first_name || '',
                                contact_last_name: pr.data.contact_last_name || p.contact_last_name || '',
                                contact_title: pr.data.contact_title || '',
                                contact_department: pr.data.contact_department || '',
                                contact_email: pr.data.contact_email || p.contact_email || '',
                                contact_phone: pr.data.contact_phone || '',
                                president_first_name: pr.data.president_first_name || '',
                                president_last_name: pr.data.president_last_name || '',
                                president_office_phone: pr.data.president_office_phone || '',
                                president_email: pr.data.president_email || '',
                                secondary_contact_first_name: pr.data.secondary_contact_first_name || '',
                                secondary_contact_last_name: pr.data.secondary_contact_last_name || '',
                                secondary_contact_title: pr.data.secondary_contact_title || '',
                                secondary_contact_department: pr.data.secondary_contact_department || '',
                                secondary_contact_email: pr.data.secondary_contact_email || '',
                                secondary_contact_phone: pr.data.secondary_contact_phone || '',
                                school_colors: pr.data.school_colors || '',
                                shipping_attention_first_name: pr.data.shipping_attention_first_name || '',
                                shipping_attention_last_name: pr.data.shipping_attention_last_name || '',
                                shipping_attention_phone: pr.data.shipping_attention_phone || '',
                                shipping_address_line1: pr.data.shipping_address_line1 || '',
                                shipping_address_line2: pr.data.shipping_address_line2 || '',
                                shipping_city: pr.data.shipping_city || '',
                                shipping_state: pr.data.shipping_state || '',
                                shipping_zip: pr.data.shipping_zip || '',
                                athletic_conference_id: pr.data.athletic_conference_id || ''
                            }));
                            // capture last-updater info if present
                            if (pr.data.last_updated_by_first_name || pr.data.updated_at) {
                                setLastUpdated({
                                    firstName: pr.data.last_updated_by_first_name,
                                    lastName: pr.data.last_updated_by_last_name,
                                    email: pr.data.last_updated_by_email,
                                    updatedAt: pr.data.updated_at || pr.data.last_saved_at
                                });
                            }
                            setIsExisting(true);
                            // capture initial snapshot for unsaved-change detection
                            initialSnapshot.current = JSON.stringify({
                                ...profile,
                                tax_id: pr.data.tax_id || '',
                                accreditation_status: pr.data.accreditation_status || '',
                                school_logo: pr.data.school_logo || '',
                                contact_first_name: pr.data.contact_first_name || profile.contact_first_name || '',
                                contact_last_name: pr.data.contact_last_name || profile.contact_last_name || '',
                                contact_title: pr.data.contact_title || '',
                                contact_department: pr.data.contact_department || '',
                                contact_email: pr.data.contact_email || profile.contact_email || '',
                                contact_phone: pr.data.contact_phone || '',
                                president_first_name: pr.data.president_first_name || '',
                                president_last_name: pr.data.president_last_name || '',
                                president_office_phone: pr.data.president_office_phone || '',
                                president_email: pr.data.president_email || '',
                                secondary_contact_first_name: pr.data.secondary_contact_first_name || '',
                                secondary_contact_last_name: pr.data.secondary_contact_last_name || '',
                                secondary_contact_title: pr.data.secondary_contact_title || '',
                                secondary_contact_department: pr.data.secondary_contact_department || '',
                                secondary_contact_email: pr.data.secondary_contact_email || '',
                                secondary_contact_phone: pr.data.secondary_contact_phone || '',
                                school_colors: pr.data.school_colors || '',
                                shipping_attention_first_name: pr.data.shipping_attention_first_name || '',
                                shipping_attention_last_name: pr.data.shipping_attention_last_name || '',
                                shipping_attention_phone: pr.data.shipping_attention_phone || '',
                                shipping_address_line1: pr.data.shipping_address_line1 || '',
                                shipping_address_line2: pr.data.shipping_address_line2 || '',
                                shipping_city: pr.data.shipping_city || '',
                                shipping_state: pr.data.shipping_state || '',
                                shipping_zip: pr.data.shipping_zip || '',
                                athletic_conference_id: pr.data.athletic_conference_id || ''
                            });
                        } else {
                            setSchoolInfoStatus('not_started');
                        }
                    } catch (err) {
                        // ignore — user can still fill the form
                    }
                    // fetch athletic conferences
                    try {
                        const ac = await api.get('/api/public/athletic-conferences');
                        setConferences(ac.data || []);
                    } catch (e) { /* ignore */ }
                    // fetch states dynamically
                    try {
                        const st = await api.get('/api/public/states');
                        setStatesList(st.data || []);
                    } catch (e) { /* ignore */ }
                } else {
                    navigate('/login');
                }
            } catch (err) {
                navigate('/login');
            }
        })();
    }, []);

    return (
        <>
            <Box component="main">
                <Grid container spacing={4} alignItems="flex-start" sx={{ mt: 2 }}>
                    <Grid item xs={12} md={3} lg={3}>
                        <LeftPanel items={[<Step key="step1">1. Fill in your details</Step>,
                            'All fields are required to move to the next step.',
                        <Step key="step2">2. Add your logo</Step>,
                            'Upload a logo (PNG, JPG, JPEG, GIF, SVG, or WEBP).',
                        <Step key="step3">3. Save your progress</Step>,
                            'You can save as a draft and finish later.',
                        <Step key="step4">4. Ready to submit?</Step>,
                            'Once submitted, this form can’t be changed.']}>
                            <PrimaryButton onClick={() => { setInfoType('faq'); setInfoOpen(true); }} sx={{ bgcolor: '#fff', color: '#000', textTransform: 'none' }}>FAQ</PrimaryButton>
                            <InfoModal open={infoOpen} onClose={() => setInfoOpen(false)} />
                        </LeftPanel>
                    </Grid>
                    <Grid item xs={12} md={9} lg={9}>
                        <Paper elevation={1} sx={{ p: { xs: 0, md: 0 }, position: 'relative', mx: 'auto', maxWidth: { xs: '100%', md: '950px' } }}>
                            <Box sx={{ position: 'sticky', top: 0, background: '#fff', zIndex: 2, p: { xs: 3, md: 4 }, borderBottom: '1px solid #eee' }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1, mb: 1 }}>
                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>School Information</Typography>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            color: schoolInfoStatus === 'submitted' ? 'success.main' : 'warning.main',
                                            fontWeight: 700,
                                        }}
                                    >
                                        {getSchoolInfoStatusLabel()}
                                    </Typography>
                                </Box>
                                <Typography variant="subtitle1" sx={{ mb: 0 }}>School: {schoolName}</Typography>
                                {lastUpdated && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Last updated by {lastUpdated.firstName || ''} {lastUpdated.lastName || ''} ({lastUpdated.email || ''}) on {formatToEastern(lastUpdated.updatedAt)}
                                    </Typography>
                                )}
                                {showSchoolInfoWarning && (
                                    <Box sx={{ mt: 2 }}>
                                        <Alert severity="warning" sx={{ fontWeight: 600 }}>
                                            School Information must be submitted before you can proceed to the Grant Selection page.
                                        </Alert>
                                    </Box>
                                )}
                            </Box>
                            <Box component="form" onSubmit={(e) => { e.preventDefault(); attemptSubmit(); }} noValidate sx={{ maxHeight: '72vh', overflow: 'auto', p: { xs: 3, md: 5 } }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Box sx={sectionSx}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        label="Tax ID"
                                                        name="tax_id"
                                                        value={profile.tax_id || ''}
                                                        onChange={handleChange}
                                                        onPaste={handleTaxIdPaste}
                                                        fullWidth
                                                        sx={fieldSx('tax_id', { mb: 2 })}
                                                        required
                                                        onBlur={() => handleBlur('tax_id')}
                                                        error={touched['tax_id'] && (!!fieldErrors['tax_id'] || (requiredFields.includes('tax_id') && !profile.tax_id))}
                                                        helperText={getHelperText('tax_id', 'Format: 12-3456789')}
                                                    />
                                                    <FormControl fullWidth error={touched['accreditation_status'] && (!!fieldErrors['accreditation_status'] || (requiredFields.includes('accreditation_status') && !profile.accreditation_status))} sx={fieldSx('accreditation_status', { mb: 2 })}>
                                                        <InputLabel id="accreditation_status-label">Accreditation Status</InputLabel>
                                                        <Select labelId="accreditation_status-label" label="Accreditation Status" name="accreditation_status" value={profile.accreditation_status || ''} onChange={handleChange} required>
                                                            {SCHOOL_PROFILE_SECTIONS.find(s => s.key === 'basic').fields.find(f => f.name === 'accreditation_status').options.map(opt => (
                                                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                                            ))}
                                                        </Select>
                                                        <FormHelperText>{getHelperText('accreditation_status')}</FormHelperText>
                                                    </FormControl>
                                                    <TextField label="School Colors" name="school_colors" value={profile.school_colors || ''} onChange={handleChange} fullWidth sx={fieldSx('school_colors', { mb: 2 })} required onBlur={() => handleBlur('school_colors')} error={touched['school_colors'] && (!!fieldErrors['school_colors'] || (requiredFields.includes('school_colors') && !profile.school_colors))} helperText={getHelperText('school_colors')} />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <div style={{ marginBottom: 16 }}>
                                                        <LogoUpload
                                                            value={profile.school_logo}
                                                            onChange={url => setProfile(p => ({ ...p, school_logo: url }))}
                                                            token={getToken()}
                                                        />
                                                        {touched['school_logo'] && fieldErrors['school_logo'] && (
                                                            <div style={{ color: 'red', fontSize: 13, marginTop: 4 }}>{fieldErrors['school_logo']}</div>
                                                        )}
                                                    </div>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Box sx={sectionSx}>
                                            <Typography variant="h6" sx={{ mt: 0, mb: 1 }}>Primary Contact</Typography>
                                            {SCHOOL_PROFILE_SECTIONS.find(s => s.key === 'primary_contact').fields.map(f => (
                                                <TextField
                                                    key={f.name}
                                                    label={f.label}
                                                    name={f.name}
                                                    value={profile[f.name] || ''}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    sx={fieldSx(f.name, { mb: 2 })}
                                                    required={!!f.required}
                                                    onBlur={() => handleBlur(f.name)}
                                                    error={touched[f.name] && (!!fieldErrors[f.name] || (requiredFields.includes(f.name) && !profile[f.name]))}
                                                    helperText={getHelperText(f.name)}
                                                    inputProps={f.type === 'phone' ? { inputMode: 'tel', maxLength: 14 } : {}}
                                                    placeholder={f.type === 'phone' ? '(555) 555-5555' : ''}
                                                />
                                            ))}
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Box sx={sectionSx}>
                                            <Typography variant="h6" sx={{ mt: 0, mb: 1 }}>Secondary Contact</Typography>
                                            {SCHOOL_PROFILE_SECTIONS.find(s => s.key === 'secondary_contact').fields.map(f => (
                                                <TextField
                                                    key={f.name}
                                                    label={f.label}
                                                    name={f.name}
                                                    value={profile[f.name] || ''}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    sx={fieldSx(f.name, { mb: 2 })}
                                                    required={!!f.required}
                                                    error={touched[f.name] && (!!fieldErrors[f.name] || (requiredFields.includes(f.name) && !profile[f.name]))}
                                                    helperText={getHelperText(f.name)}
                                                    inputProps={f.type === 'phone' ? { inputMode: 'tel', maxLength: 14 } : {}}
                                                    placeholder={f.type === 'phone' ? '(555) 555-5555' : ''}
                                                />
                                            ))}
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Box sx={sectionSx}>
                                            <Typography variant="h6" sx={{ mt: 0, mb: 1 }}>President</Typography>
                                            <Grid container spacing={2}>
                                                {SCHOOL_PROFILE_SECTIONS.find(s => s.key === 'president').fields.map(f => (
                                                    <Grid item xs={12} md={6} key={f.name}>
                                                        <TextField
                                                            label={f.label}
                                                            name={f.name}
                                                            value={profile[f.name] || ''}
                                                            onChange={handleChange}
                                                            fullWidth
                                                            sx={fieldSx(f.name, { mb: 2 })}
                                                            required={!!f.required}
                                                            error={touched[f.name] && (!!fieldErrors[f.name] || (requiredFields.includes(f.name) && !profile[f.name]))}
                                                            helperText={getHelperText(f.name)}
                                                            inputProps={f.type === 'phone' ? { inputMode: 'tel', maxLength: 14 } : {}}
                                                            placeholder={f.type === 'phone' ? '(555) 555-5555' : ''}
                                                        />
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Box sx={sectionSx}>
                                            <Typography variant="h6" sx={{ mt: 0, mb: 1 }}>Shipping</Typography>
                                            {SCHOOL_PROFILE_SECTIONS.find(s => s.key === 'shipping').fields.map(f => {
                                                // shipping_state renders the state select AND the ZIP field inline;
                                                // skip the standalone `shipping_zip` field to avoid duplicate rendering.
                                                if (f.name === 'shipping_zip') return null;
                                                if (f.type === 'states') {
                                                    return (
                                                        <Grid container spacing={2} key={f.name}>
                                                            <Grid item xs={12} md={6}>
                                                                <FormControl fullWidth error={touched[f.name] && (!!fieldErrors[f.name] || (requiredFields.includes(f.name) && !profile[f.name]))} sx={fieldSx(f.name, { mb: 2 })}>
                                                                    <InputLabel id={`${f.name}-label`}>{f.label}</InputLabel>
                                                                    <Select labelId={`${f.name}-label`} label={f.label} name={f.name} value={profile[f.name] || ''} onChange={handleChange} required={!!f.required}>
                                                                        {statesList.map(s => (<MenuItem key={s.name || s} value={s.name || s}>{s.name || s}</MenuItem>))}
                                                                    </Select>
                                                                    <FormHelperText>{getHelperText(f.name)}</FormHelperText>
                                                                </FormControl>
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <TextField label="Shipping ZIP" name="shipping_zip" value={profile.shipping_zip || ''} onChange={handleChange} fullWidth sx={fieldSx('shipping_zip', { mb: 2 })} required error={touched['shipping_zip'] && (!!fieldErrors['shipping_zip'] || (requiredFields.includes('shipping_zip') && !profile.shipping_zip))} helperText={getHelperText('shipping_zip')} />
                                                            </Grid>
                                                        </Grid>
                                                    );
                                                }
                                                if (f.type === 'conferences') {
                                                    return (
                                                        <FormControl fullWidth error={touched[f.name] && (!!fieldErrors[f.name] || (requiredFields.includes(f.name) && !profile[f.name]))} sx={fieldSx(f.name, { mb: 2 })} key={f.name}>
                                                            <InputLabel id={`${f.name}-label`}>{f.label}</InputLabel>
                                                            <Select labelId={`${f.name}-label`} label={f.label} name={f.name} value={profile[f.name] || ''} onChange={handleChange} required={!!f.required}>
                                                                {conferenceOptions.map(c => {
                                                                    const isSpecial = SPECIAL_CONFERENCE_IDS.has(Number(c?.id));
                                                                    return (
                                                                        <MenuItem
                                                                            key={c.id}
                                                                            value={c.id}
                                                                            sx={isSpecial ? { color: '#F96302', fontWeight: 700 } : {}}
                                                                        >
                                                                            {c.name}
                                                                        </MenuItem>
                                                                    );
                                                                })}
                                                            </Select>
                                                            <FormHelperText>{getHelperText(f.name)}</FormHelperText>
                                                        </FormControl>
                                                    );
                                                }
                                                return (
                                                    <TextField key={f.name} label={f.label} name={f.name} value={profile[f.name] || ''} onChange={handleChange} fullWidth sx={fieldSx(f.name, { mb: 2 })} required={!!f.required} error={touched[f.name] && (!!fieldErrors[f.name] || (requiredFields.includes(f.name) && !profile[f.name]))} helperText={getHelperText(f.name)} inputProps={f.type === 'phone' ? { inputMode: 'tel', maxLength: 14 } : {}} placeholder={f.type === 'phone' ? '(555) 555-5555' : ''} />
                                                );
                                            })}
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                            <Button variant="outlined" onClick={handleSaveDraft} disabled={submitting}>Save Draft</Button>
                                            <PrimaryButton type="submit" disabled={submitting}>{'Submit'}</PrimaryButton>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
                        <ConfirmSubmissionModal open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={async () => { setConfirmOpen(false); await handleSubmit(); }} />
                        <UnsavedChangesModal open={unsavedModalOpen} onClose={handleModalClose} onConfirm={handleModalConfirm} title="Unsaved Changes" message={"It looks like you have unsaved changes on your Campus Information Form. Click Cancel to return to your form, then scroll to the bottom and save your work or hit \"OK\" to lose your changes."} />
                    </Grid>
                </Grid>
            </Box>
            <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </>
    );
}

export default SchoolInfo;
